/**
 * Provisional organic paint on an extruded cap. Not height, not lighting.
 * Authored on `MapDocument.look` (capFlockEnabled / capFlockAmount). We may
 * delete this module and rebuild. See docs/CAP-FLOCK.md.
 *
 * Collision stays on the waterline.
 */

import { signedArea } from '../geometry.ts';
import { clamp } from '../math.ts';
import { hashSeed, mulberry32, randomBetween } from '../rng.ts';
import { closestPointOnSegment, distance } from './document.ts';
import { pointInPolygon } from './islands.ts';
import type { Vec2 } from './types.ts';

export type CapKind = 'island' | 'rock';

/** Paint flock on extruded caps. Debugger / View can turn this off. */
export const DEFAULT_CAP_FLOCK_ENABLED = true;
/** 0 = fewest ovals, 0.5 = shipped density, 1 = twice that. */
export const DEFAULT_CAP_FLOCK_AMOUNT = 0.5;
/** Tiny screen-up sit so an oval reads as a bump, not a sticker. */
export const CAP_BLOB_RISE_PX = 3;
/** Dark oval under the bump. Deck paint only — not water occupancy. */
export const CAP_BLOB_CONTACT_MIX = 0.2;
export const CAP_BLOB_CONTACT_ALPHA = 0.32;

export type CapBlob = {
  x: number;
  y: number;
  rx: number;
  ry: number;
  mix: number;
};

/** How far mid / crown sit from the centroid (1 = the cap outline). */
export const CAP_MID_SCALE = 0.78;
export const CAP_CROWN_SCALE = 0.55;
/** Mix toward 0xfff1c8 on the crown. */
export const CAP_CROWN_MIX = 0.07;
/** Mix toward 0x1a1210 on the mid inset. */
export const CAP_MID_MIX = 0.05;
export const CAP_LIP_PX = 11;
export const CAP_LIP_MIX = 0.16;
export const CAP_LIP_ALPHA = 0.32;
export const CAP_BLOB_AREA_PX2 = 22_000;
export const CAP_BLOB_MIN_ISLAND = 3;
export const CAP_BLOB_MIN_ROCK = 2;
export const CAP_BLOB_MAX_ISLAND = 12;
export const CAP_BLOB_MAX_ROCK = 7;
export const CAP_BLOB_EDGE_PX = 18;
/** Skip contour insets unless the centroid sits this far inside the cap. */
export const CAP_INSET_MIN_CLEAR_PX = 40;
/** Skip contour insets when the cap is a ribbon (area / bbox). Circles are ~0.78. */
export const CAP_INSET_MIN_FILL = 0.5;
const CAP_BLOB_ATTEMPTS = 24;
const MIN_INSET_AREA = 4;

export function capPolygonCentroid(points: readonly Vec2[]): Vec2 | null {
  let twiceArea = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    if (!a || !b) {
      continue;
    }

    const cross = a.x * b.y - b.x * a.y;
    twiceArea += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }

  if (Math.abs(twiceArea) < 1e-8) {
    return null;
  }

  return { x: cx / (3 * twiceArea), y: cy / (3 * twiceArea) };
}

/**
 * Contour insets only on fat, compact caps. A crescent’s centroid sits in the
 * hollow or in a thin neck — shrinking toward it copies the shoreline into ribbons.
 */
export function capAllowsInset(points: readonly Vec2[]): boolean {
  if (points.length < 3) {
    return false;
  }

  const centroid = capPolygonCentroid(points);

  if (!centroid || !pointInPolygon(centroid, points)) {
    return false;
  }

  if (distanceToRing(centroid, points) < CAP_INSET_MIN_CLEAR_PX) {
    return false;
  }

  return capFillRatio(points) >= CAP_INSET_MIN_FILL;
}

function capFillRatio(points: readonly Vec2[]): number {
  const first = points[0];

  if (!first) {
    return 0;
  }

  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];

    if (!point) {
      continue;
    }

    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  const box = (maxX - minX) * (maxY - minY);

  if (box <= 1e-6) {
    return 0;
  }

  return Math.abs(signedArea(points)) / box;
}

/** `scale` is the remaining radius from the centroid (0.55 = crown). */
export function scaleTowardCentroid(points: readonly Vec2[], scale: number): Vec2[] | null {
  if (!capAllowsInset(points) || scale <= 0 || scale >= 1) {
    return null;
  }

  const centroid = capPolygonCentroid(points);

  if (!centroid) {
    return null;
  }

  const next = points.map((point) => ({
    x: centroid.x + (point.x - centroid.x) * scale,
    y: centroid.y + (point.y - centroid.y) * scale,
  }));

  if (Math.abs(signedArea(next)) < MIN_INSET_AREA) {
    return null;
  }

  return next;
}

/** 0–2 scale. Factory amount 0.5 is the density shipped before the slider. */
export function capFlockDensity(amount: number): number {
  return clamp(amount, 0, 1) * 2;
}

export function capBlobCount(
  area: number,
  kind: CapKind,
  amount = DEFAULT_CAP_FLOCK_AMOUNT,
): number {
  const density = capFlockDensity(amount);

  if (density <= 0) {
    return 0;
  }

  const raw = Math.max(0, area) / CAP_BLOB_AREA_PX2;
  const scaled = (kind === 'rock' ? raw * 0.55 : raw) * density;
  const min = (kind === 'rock' ? CAP_BLOB_MIN_ROCK : CAP_BLOB_MIN_ISLAND) * density;
  const max = (kind === 'rock' ? CAP_BLOB_MAX_ROCK : CAP_BLOB_MAX_ISLAND) * density;

  return clamp(Math.round(scaled), Math.round(min), Math.round(max));
}

export function capFlockBlobs(
  cap: readonly Vec2[],
  id: string,
  kind: CapKind,
  amount = DEFAULT_CAP_FLOCK_AMOUNT,
): CapBlob[] {
  if (cap.length < 3 || id.length === 0) {
    return [];
  }

  const area = Math.abs(signedArea(cap));
  const want = capBlobCount(area, kind, amount);
  const attempts = Math.round(CAP_BLOB_ATTEMPTS * (1 + clamp(amount, 0, 1)));
  const pack = 0.55 * (1.15 - 0.4 * clamp(amount, 0, 1));
  const rng = mulberry32(hashSeed(id, 'cap'));
  const first = cap[0];

  if (!first) {
    return [];
  }

  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;

  for (let i = 1; i < cap.length; i += 1) {
    const point = cap[i];

    if (!point) {
      continue;
    }

    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  const width = maxX - minX;
  const height = maxY - minY;
  const blobs: CapBlob[] = [];

  for (let placed = 0; placed < want; placed += 1) {
    let found: CapBlob | null = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const candidate = {
        x: minX + rng() * width,
        y: minY + rng() * height,
      };

      if (!pointInPolygon(candidate, cap)) {
        continue;
      }

      if (distanceToRing(candidate, cap) < CAP_BLOB_EDGE_PX) {
        continue;
      }

      const rx =
        kind === 'rock' ? randomBetween(rng, 8, 18) : randomBetween(rng, 12, 28);
      const flatten =
        kind === 'rock' ? randomBetween(rng, 0.4, 0.7) : randomBetween(rng, 0.55, 0.85);
      const mix =
        kind === 'rock' ? randomBetween(rng, 0.05, 0.09) : randomBetween(rng, 0.04, 0.07);

      if (blobsTooClose(candidate, rx, blobs, pack)) {
        continue;
      }

      found = { x: candidate.x, y: candidate.y, rx, ry: rx * flatten, mix };
      break;
    }

    if (found) {
      blobs.push(found);
    }
  }

  return blobs;
}

function distanceToRing(point: Vec2, ring: readonly Vec2[]): number {
  let best = Number.POSITIVE_INFINITY;

  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];

    if (!a || !b) {
      continue;
    }

    best = Math.min(best, distance(point, closestPointOnSegment(point, a, b)));
  }

  return best;
}

function blobsTooClose(
  point: Vec2,
  rx: number,
  blobs: readonly CapBlob[],
  pack: number,
): boolean {
  for (const blob of blobs) {
    const gap = (rx + blob.rx) * pack;

    if (distance(point, blob) < gap) {
      return true;
    }
  }

  return false;
}
